import { getUserPref, setUserPref } from '../hooks/useUserPreferences';

/**
 * The facilities a user opened most recently, per connection.
 *
 * Kept in localStorage under the user's preferences (`dietech_prefs_{userId}_recentFacilities`).
 * It sorts the facility switcher and, at sign-in, opens the facility the user last used when they
 * have no default facility (resolveFacilityFromMappings).
 *
 * Every entry carries the connection it was used in. A facility number means nothing outside its
 * own database: facility 27 in one connection is a different building in another, and the same
 * PKey_User can be a different person there. Reads therefore only ever return entries of the
 * connection asked about; entries written before the connection was recorded are ignored.
 *
 * This is a preference, never a permission: the resolver only honours an id that is also in the
 * user's own tblPMSec_Fac_Dietech mappings.
 */

const RECENT_FACILITIES_KEY = 'recentFacilities';
/** Kept per connection. */
const MAX_RECENT_PER_CONNECTION = 5;
/** Across all connections, so a PSI user who visits many organizations does not grow it forever. */
const MAX_RECENT_TOTAL = 50;

export interface RecentFacility {
  id: number;
  name: string;
  /** When it was last opened (ms). */
  lastUsed: number;
  /** PKey_Connection it was used in. Entries without one predate it and are ignored. */
  connectionId?: number;
}

function readAll(userId: number): RecentFacility[] {
  const raw = getUserPref<unknown>(userId, RECENT_FACILITIES_KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is RecentFacility =>
      !!r && typeof r === 'object' && typeof (r as RecentFacility).id === 'number' && (r as RecentFacility).id > 0,
  );
}

const ofConnection = (connectionId: number) => (r: RecentFacility) =>
  typeof r.connectionId === 'number' && r.connectionId > 0 && r.connectionId === connectionId;

/** The user's recent facilities in this connection, most recent first. */
export function getRecentFacilities(userId: number, connectionId: number | null | undefined): RecentFacility[] {
  if (userId <= 0 || !connectionId || connectionId <= 0) return [];
  return readAll(userId)
    .filter(ofConnection(connectionId))
    .sort((a, b) => (b.lastUsed ?? 0) - (a.lastUsed ?? 0));
}

/** Facility ids only, most recent first (what the sign-in resolver takes). */
export function getRecentFacilityIds(userId: number, connectionId: number | null | undefined): number[] {
  return getRecentFacilities(userId, connectionId).map((r) => r.id);
}

/**
 * Record that the user is now in this facility of this connection. Called for every facility
 * change: a pick in the switcher, the facility resolved at sign-in, a window opened for a facility.
 * Without a connection there is nothing safe to record it against, so it is skipped.
 */
export function recordRecentFacility(
  userId: number,
  connectionId: number | null | undefined,
  facilityId: number,
  facilityName: string,
  now: number = Date.now(),
): void {
  if (userId <= 0 || !connectionId || connectionId <= 0 || facilityId <= 0) return;
  // Unscoped entries (written before the connection was recorded) cannot be trusted: drop them.
  const all = readAll(userId).filter((r) => typeof r.connectionId === 'number' && r.connectionId > 0);
  const same = ofConnection(connectionId);
  const thisConnection = all.filter((r) => same(r) && r.id !== facilityId);
  const others = all.filter((r) => !same(r));
  const next = [
    { id: facilityId, name: facilityName, lastUsed: now, connectionId },
    ...thisConnection.sort((a, b) => b.lastUsed - a.lastUsed).slice(0, MAX_RECENT_PER_CONNECTION - 1),
    ...others.sort((a, b) => b.lastUsed - a.lastUsed),
  ].slice(0, MAX_RECENT_TOTAL);
  setUserPref(userId, RECENT_FACILITIES_KEY, next);
}
